import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PublicLayout from "@/layouts/PublicLayout";
import { toast } from "sonner";
import { Mail, Check, Newspaper } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function DigestSubscribePage() {
  const { t } = useLang();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ email: "", first_name: "", language: "fr" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/digest/categories").then((r) => {
      setCategories(r.data.categories || []);
      // All checked by default
      setSelected(new Set((r.data.categories || []).map((c) => c.code)));
    }).catch(() => {});
  }, []);

  const toggle = (code) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Email obligatoire");
    if (selected.size === 0) return toast.error("Sélectionnez au moins une catégorie");
    try {
      setSubmitting(true);
      await api.post("/digest/subscribe", {
        ...form,
        categories: Array.from(selected),
      });
      setDone(true);
      toast.success("Inscription confirmée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50 text-emerald-600">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_digestsubscribepage_90")}</h1>
          <p className="mt-3 text-neutral-600">{t("comp_digestsubscribepage_91")}</p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-12">
        <div className="text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-wine-50 text-wine-700">
            <Newspaper className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-serif text-4xl text-navy">Weekly Digest</h1>
          <p className="mt-3 text-neutral-600">{t("comp_digestsubscribepage_92")}</p>
        </div>

        <form onSubmit={submit} className="mt-10 rounded-3xl border border-neutral-100 bg-white p-8 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("comp_digestsubscribepage_93")}</label>
              <input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm focus:border-wine-400 focus:outline-none focus:ring-1 focus:ring-wine-200"
                placeholder="Marie"
                data-testid="digest-first-name"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Langue</label>
              <select
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm focus:border-wine-400 focus:outline-none focus:ring-1 focus:ring-wine-200"
                data-testid="digest-language"
              >
                <option value="fr">{t("comp_digestsubscribepage_94")}</option>
                <option value="en">🇬🇧 English</option>
                <option value="es">🇪🇸 Español</option>
                <option value="it">🇮🇹 Italiano</option>
                <option value="de">🇩🇪 Deutsch</option>
                <option value="pt">{t("comp_digestsubscribepage_95")}</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Email <span className="text-rose-500">*</span></label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-10 py-3 text-sm focus:border-wine-400 focus:outline-none focus:ring-1 focus:ring-wine-200"
                placeholder="marie@maison-vins.fr"
                data-testid="digest-email"
              />
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("comp_digestsubscribepage_96")} <span className="text-neutral-400">{t("comp_digestsubscribepage_97")}</span></p>
            <div className="mt-3 space-y-2">
              {categories.map((cat) => (
                <label
                  key={cat.code}
                  className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 text-sm transition ${
                    selected.has(cat.code)
                      ? "border-wine-300 bg-wine-50/40"
                      : "border-neutral-200 bg-white"
                  }`}
                  data-testid={`digest-cat-${cat.code}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(cat.code)}
                    onChange={() => toggle(cat.code)}
                    className="h-4 w-4 rounded border-neutral-300 text-wine-600"
                  />
                  <span className="font-medium text-neutral-800">{cat.label}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full rounded-full bg-wine-600 px-6 py-3 text-sm font-bold text-white hover:bg-wine-700 disabled:opacity-50"
            data-testid="digest-submit"
          >
            {submitting ? "Inscription en cours..." : "Je m'inscris au Weekly Digest"}
          </button>
          <p className="mt-3 text-center text-[11px] text-neutral-400">
            Vous pourrez modifier vos préférences ou vous désinscrire à tout moment depuis n&apos;importe quel email reçu.
          </p>
        </form>
      </div>
    </PublicLayout>
  );
}
